import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import client, { apiErr } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Send, MessageSquare, Search, Building2, ArrowLeft, User, Users, ClipboardList, Check, CheckCheck, UserPlus, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { usePoll } from "@/hooks/usePoll";
import { confirm } from "@/components/ConfirmDialog";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fileUrl = (id) => `${BACKEND_URL}/api/files/${id}/download?auth=${localStorage.getItem("iclean_token") || ""}`;

function MessageBody({ m }) {
  const atts = m.attachments && m.attachments.length ? m.attachments : null;
  const marker = !atts && typeof m.body === "string" ? m.body.match(/^img::(.+)$/) : null;
  if (atts) {
    return (
      <div className="space-y-1.5">
        {atts.map((id) => (
          <a key={id} href={fileUrl(id)} target="_blank" rel="noreferrer">
            <img src={fileUrl(id)} alt="attachment" data-testid="msg-attachment-img"
              className="rounded-lg max-w-[220px] max-h-[240px] object-cover border border-black/5" />
          </a>
        ))}
        {m.body && !m.body.startsWith("img::") && <p className="text-sm whitespace-pre-wrap">{m.body}</p>}
      </div>
    );
  }
  if (marker) {
    return (
      <a href={fileUrl(marker[1])} target="_blank" rel="noreferrer">
        <img src={fileUrl(marker[1])} alt="attachment" data-testid="msg-attachment-img"
          className="rounded-lg max-w-[220px] max-h-[240px] object-cover border border-black/5" />
      </a>
    );
  }
  return <p className="text-sm whitespace-pre-wrap">{m.body}</p>;
}


const timeAgo = (iso) => {
  if (!iso) return "";
  const d = new Date(iso), now = new Date();
  if (d.toDateString() === now.toDateString()) return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  if (new Date(now - 86400000).toDateString() === d.toDateString()) return "Yesterday";
  return d.toLocaleDateString();
};
const ICON = { direct: User, site: Building2, job: ClipboardList };

export default function Messages() {
  const { t } = useI18n();
  const { can, user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [convs, setConvs] = useState([]);
  const [active, setActive] = useState(null); // {type, key, title, siteId?, jobId?, otherId?}
  const [thread, setThread] = useState([]);
  const [body, setBody] = useState("");
  const [q, setQ] = useState("");
  const [receipts, setReceipts] = useState([]);
  const [compose, setCompose] = useState(false);
  const [recips, setRecips] = useState([]);
  const [recipQ, setRecipQ] = useState("");
  const endRef = useRef(null);

  const loadConvs = useCallback(() => client.get(`/messages/conversations?type=all`).then((r) => setConvs(r.data)).catch(() => setConvs([])), []);
  useEffect(() => { loadConvs(); }, [loadConvs]);

  const threadQuery = useCallback((a) => {
    if (a.type === "direct") return a.oversight ? `scope=direct&dmKey=${a.key}` : `scope=direct&userId=${a.otherId}`;
    if (a.type === "job") return `scope=job&jobId=${a.jobId}`;
    if (a.type === "property") return `scope=property&propertyId=${a.propertyId}`;
    return `scope=site&siteId=${a.siteId}`;
  }, []);

  const openConv = useCallback(async (a) => {
    setActive(a); setThread([]); setReceipts([]);
    try {
      const { data } = await client.get(`/messages?${threadQuery(a)}`);
      setThread(data.slice().reverse());
      client.get(`/messages/receipts?${threadQuery(a)}`).then((r) => setReceipts(r.data || [])).catch(() => {});
      if (a.key && !a.oversight) { client.post("/messages/read", { convKey: a.key }).then(loadConvs).catch(() => {}); }
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  }, [threadQuery, loadConvs]);

  const refreshActive = useCallback(() => {
    if (!active) return;
    client.get(`/messages?${threadQuery(active)}`).then((r) => setThread(r.data.slice().reverse())).catch(() => {});
    client.get(`/messages/receipts?${threadQuery(active)}`).then((r) => setReceipts(r.data || [])).catch(() => {});
  }, [active, threadQuery]);
  usePoll(() => { loadConvs(); refreshActive(); }, 5000);

  // Deep-links: ?dm=<userId> | ?job=<jobId> | ?site=<siteId>
  useEffect(() => {
    const dm = params.get("dm"), job = params.get("job"), site = params.get("site");
    if (dm) { openConv({ type: "direct", otherId: dm, key: null, title: params.get("name") || "Direct" }); }
    else if (job) { openConv({ type: "job", jobId: job, key: "job_" + job, title: `Job #${String(job).slice(-6).toUpperCase()}` }); }
    else if (site) { openConv({ type: "site", siteId: site, key: "site_" + site, title: params.get("name") || "Site" }); }
    if (dm || job || site) setParams({}, { replace: true });
    // eslint-disable-next-line
  }, []);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [thread.length, active]);

  const send = async () => {
    if (!body.trim() || !active) return;
    const payload = { body };
    if (active.type === "direct") { payload.scope = "direct"; payload.toUserId = active.otherId; }
    else if (active.type === "job") { payload.scope = "job"; payload.jobId = active.jobId; }
    else if (active.type === "property") { payload.scope = "property"; payload.propertyId = active.propertyId; }
    else { payload.scope = "site"; payload.siteId = active.siteId; }
    try {
      await client.post("/messages", payload);
      setBody("");
      const { data } = await client.get(`/messages?${threadQuery(active)}`);
      setThread(data.slice().reverse());
      loadConvs();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const startDM = (u) => { setCompose(false); setRecipQ(""); openConv({ type: "direct", otherId: u.id, key: null, title: u.displayName }); };

  const canDelete = user?.role === "owner" || user?.role === "admin";
  const deleteConv = async (c) => {
    if (!c.key) return;
    if (!(await confirm({ title: t("msg.delTitle", "Delete chat"), description: `${t("msg.delConfirm", "Permanently delete this conversation")}${c.title ? ` — "${c.title}"` : ""}? ${t("common.cannotUndo", "This cannot be undone.")}`, confirmText: t("common.delete", "Delete"), destructive: true }))) return;
    try {
      const { data } = await client.delete(`/messages/conversations?key=${encodeURIComponent(c.key)}`);
      toast.success(`${t("msg.deleted", "Chat deleted")}${data.deleted ? ` (${data.deleted})` : ""}`);
      if (active?.key === c.key) setActive(null);
      loadConvs();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  useEffect(() => {
    if (!compose) return;
    client.get(`/messages/recipients${recipQ ? `?q=${encodeURIComponent(recipQ)}` : ""}`).then((r) => setRecips(r.data)).catch(() => setRecips([]));
  }, [compose, recipQ]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return convs;
    return convs.filter((c) => [c.title, c.subtitle, c.lastBody].filter(Boolean).some((v) => v.toLowerCase().includes(s)));
  }, [convs, q]);

  const people = useMemo(() => filtered.filter((c) => c.type === "direct" && !c.oversight), [filtered]);
  const groups = useMemo(() => filtered.filter((c) => c.type !== "direct"), [filtered]);
  const monitored = useMemo(() => filtered.filter((c) => c.type === "direct" && c.oversight), [filtered]);

  const ConvButton = (c) => {
    const Ic = ICON[c.type] || MessageSquare;
    return (
      <div key={c.key} className="relative group">
        <button data-testid={`conversation-${c.key}`} onClick={() => openConv(c)}
          className={`w-full flex items-start gap-3 px-3 py-2.5 ${canDelete ? "pr-9" : ""} border-b border-slate-50 text-left hover:bg-slate-50 ${active?.key === c.key ? "bg-brand/5" : ""}`}>
          <div className={`h-8 w-8 rounded-full flex items-center justify-center shrink-0 ${c.type === "direct" ? "bg-purple-100 text-purple-600" : c.type === "job" ? "bg-brand/10 text-brand" : "bg-teal-100 text-teal-700"}`}><Ic className="h-4 w-4" /></div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-2">
              <span className="font-semibold text-sm text-slate-900 truncate">{c.title}</span>
              <span className="text-[10px] text-slate-400 shrink-0">{timeAgo(c.lastAt)}</span>
            </div>
            <p className="text-xs text-slate-500 truncate mt-0.5">{c.subtitle ? <span className="text-slate-400">{c.subtitle} · </span> : null}{c.lastSender ? <span className="font-medium text-slate-600">{c.lastSender}: </span> : null}{c.lastBody}</p>
          </div>
          {c.unread > 0 && <Badge data-testid={`unread-${c.key}`} className="bg-brand text-white shrink-0">{c.unread}</Badge>}
        </button>
        {canDelete && c.key && (
          <button data-testid={`conversation-delete-${c.key}`} onClick={(e) => { e.stopPropagation(); deleteConv(c); }} title={t("msg.delTitle", "Delete chat")}
            className="absolute right-2 top-2.5 h-7 w-7 flex items-center justify-center rounded-md text-slate-300 opacity-0 group-hover:opacity-100 hover:text-red-600 hover:bg-red-50 transition-opacity">
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4" data-testid="messages-page">
      <div>
        <h2 className="font-display text-2xl font-bold text-brand-ink">{t("msg.title", "Messages")}</h2>
        <p className="text-sm text-slate-500">{t("msg.subtitle2", "Direct messages and site / group conversations, kept separate.")}</p>
      </div>

      <Card className="border-slate-200 overflow-hidden">
        <div className="grid md:grid-cols-[320px_1fr] h-[72vh] min-h-0">
          <div className={`border-r border-slate-200 flex flex-col min-h-0 ${active ? "hidden md:flex" : "flex"}`}>
            <div className="p-3 border-b border-slate-100 space-y-2">
              {can("message.send") && (
                <Button data-testid="messages-new-btn" onClick={() => setCompose(true)} className="w-full bg-brand hover:bg-brand-dark">
                  <UserPlus className="h-4 w-4 mr-2" />{t("msg.new", "New message")}
                </Button>
              )}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input data-testid="messages-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("msg.searchConv", "Search conversations…")} className="pl-9 h-9" />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto min-h-0" data-testid="conversation-list">
              {filtered.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-slate-400 text-sm"><MessageSquare className="h-8 w-8 mb-2" />{t("msg.none", "No conversations yet.")}</div>
              ) : (
                <>
                  <div data-testid="section-people">
                    <div className="sticky top-0 z-10 px-3 py-1.5 bg-slate-50 border-b border-slate-100 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                      <User className="h-3 w-3" /> {t("msg.sec.people", "Direct messages")}
                    </div>
                    {people.length === 0
                      ? <p className="px-3 py-2 text-xs text-slate-400">{t("msg.noPeople", "No direct messages yet.")}</p>
                      : people.map(ConvButton)}
                  </div>
                  <div data-testid="section-groups">
                    <div className="sticky top-0 z-10 px-3 py-1.5 bg-slate-50 border-b border-slate-100 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                      <Building2 className="h-3 w-3" /> {t("msg.sec.groups", "Sites & Groups")}
                    </div>
                    {groups.length === 0
                      ? <p className="px-3 py-2 text-xs text-slate-400">{t("msg.noGroups", "No site or group conversations yet.")}</p>
                      : groups.map(ConvButton)}
                  </div>
                  {monitored.length > 0 && (
                    <div data-testid="section-monitored">
                      <div className="sticky top-0 z-10 px-3 py-1.5 bg-amber-50 border-b border-amber-100 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-amber-700">
                        <Users className="h-3 w-3" /> {t("msg.sec.monitored", "Chats between users (oversight)")}
                      </div>
                      {monitored.map(ConvButton)}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          <div className={`flex flex-col min-h-0 ${active ? "flex" : "hidden md:flex"}`}>
            {!active ? (
              <div className="flex-1 flex flex-col items-center justify-center text-slate-400"><MessageSquare className="h-10 w-10 mb-2" />{t("msg.selectConv", "Select a conversation")}</div>
            ) : (
              <>
                <div className="h-14 flex items-center gap-2 px-4 border-b border-slate-100 shrink-0">
                  <Button variant="ghost" size="icon" data-testid="thread-back" onClick={() => setActive(null)}><ArrowLeft className="h-4 w-4" /></Button>
                  <Badge variant="outline" className="capitalize">{t(`msg.type.${active.type}`, active.type)}</Badge>
                  {active.oversight && <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200" data-testid="thread-oversight">{t("msg.oversight", "Oversight (read-only)")}</Badge>}
                  <span className="font-semibold text-slate-900 truncate" data-testid="thread-title">{active.title}</span>
                  {canDelete && active.key && (
                    <Button variant="ghost" size="icon" data-testid="thread-delete" onClick={() => deleteConv(active)} title={t("msg.delTitle", "Delete chat")} className="ml-auto text-slate-400 hover:text-red-600 hover:bg-red-50">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                <div className="flex-1 overflow-y-auto min-h-0 p-4 space-y-3 bg-slate-50/50" data-testid="thread-messages">
                  {(() => {
                    const lastMine = [...thread].reverse().find((m) => m.senderId === user?.id);
                    const readers = lastMine ? receipts.filter((r) => r.lastReadAt && r.lastReadAt >= lastMine.createdAt) : [];
                    return thread.map((m) => {
                      const mine = m.senderId === user?.id;
                      const showReceipt = mine && lastMine && m.id === lastMine.id;
                      return (
                        <div key={m.id} data-testid={`message-${m.id}`} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                          <div className={`max-w-[75%] rounded-2xl px-4 py-2 ${mine ? "bg-brand text-white rounded-br-sm" : "bg-white border border-slate-200 rounded-bl-sm"}`}>
                            {!mine && <p className="text-[11px] font-semibold text-brand mb-0.5">{m.senderName}</p>}
                            <MessageBody m={m} />
                            <p className={`text-[10px] mt-1 ${mine ? "text-white/70" : "text-slate-400"}`}>{new Date(m.createdAt).toLocaleString()}</p>
                            {showReceipt && (
                              <p className="text-[10px] mt-0.5 flex items-center justify-end gap-1 text-white/90" data-testid="message-receipt">
                                {readers.length > 0
                                  ? <><CheckCheck className="h-3 w-3" />{active.type === "direct" ? t("msg.read", "Read") : `${t("msg.readBy", "Read by")} ${readers.length}`}</>
                                  : <><Check className="h-3 w-3" />{t("msg.delivered", "Delivered")}</>}
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    });
                  })()}
                  <div ref={endRef} />
                </div>
                {can("message.send") && !active.oversight && (
                  <div className="border-t border-slate-200 p-3 flex gap-2 shrink-0">
                    <Input data-testid="message-input" value={body} onChange={(e) => setBody(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && send()} placeholder={t("msg.placeholder", "Write a message…")} />
                    <Button data-testid="message-send-btn" onClick={send} className="bg-brand hover:bg-brand-dark"><Send className="h-4 w-4" /></Button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </Card>

      <Dialog open={compose} onOpenChange={setCompose}>
        <DialogContent data-testid="compose-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><UserPlus className="h-5 w-5 text-brand" />{t("msg.new", "New message")}</DialogTitle></DialogHeader>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input autoFocus data-testid="compose-search" value={recipQ} onChange={(e) => setRecipQ(e.target.value)} placeholder={t("msg.searchPeople", "Search people…")} className="pl-9" />
          </div>
          <div className="max-h-[50vh] overflow-y-auto -mx-2" data-testid="compose-list">
            {recips.length === 0 ? (
              <p className="px-3 py-6 text-center text-sm text-slate-400">{t("msg.noPeopleFound", "No people found.")}</p>
            ) : recips.map((u) => (
              <button key={u.id} data-testid={`compose-recipient-${u.id}`} onClick={() => startDM(u)}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left hover:bg-slate-50">
                <div className="h-8 w-8 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center text-xs font-bold uppercase shrink-0">
                  {(u.displayName || "?").split(" ").map((s) => s[0]).slice(0, 2).join("")}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-900 truncate">{u.displayName}</p>
                  <p className="text-xs text-slate-500">{t(`role.${u.role}`, u.role)}</p>
                </div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
